package com.shadowings.gero.presentation.screen

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Image
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.shadowings.gero.data.repository.Hiero
import com.shadowings.gero.data.repository.UniliteralRepository
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.ui.LocalIsDarkMode
import com.shadowings.gero.presentation.ui.hieroglyphTint
import com.shadowings.gero.presentation.util.DeviceClass
import com.shadowings.gero.presentation.util.DevicePreviews
import com.shadowings.gero.presentation.util.rememberDeviceInfo
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.favourite_on
import gero.composeapp.generated.resources.favorite_badge_desc
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

@Composable
fun HieroItemCard(
    hiero: Hiero,
    seen: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    isFavorite: Boolean = false
) {
    val hideCode = LocalDensity.current.fontScale > 1.49f
    val deviceInfo = rememberDeviceInfo()
    val symbolSize = if (deviceInfo.deviceClass == DeviceClass.WEB_FULL) 52.dp else 32.dp
    val containerColor = if (seen) {
        MaterialTheme.colorScheme.surfaceContainer
    } else {
        MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f)
    }

    val borderColor = if (seen) {
        MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.5f)
    } else {
        MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.2f)
    }

    val hapticFeedback = LocalHapticFeedback.current
    Box(modifier = modifier.aspectRatio(1f)) {
        Card(
            modifier = Modifier
                .fillMaxSize()
                .clickable {
                    hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                    onClick()
                }.semantics {
                    testTag =
                        "compendio_hiero_card_${hiero.gardinerCode}"
                },
            shape = RoundedCornerShape(12.dp),
            colors = CardDefaults.cardColors(containerColor = containerColor),
            border = BorderStroke(1.5.dp, borderColor)
        ) {
            Column(
                modifier = Modifier.fillMaxSize().padding(4.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center
            ) {
                val res = UniliteralRepository.resFromGardinerCode(hiero.gardinerCode)
                if (res != null) {
                    Image(
                        painter = painterResource(res),
                        contentDescription = hiero.gardinerCode,
                        modifier = Modifier.size(symbolSize).weight(1f),
                        colorFilter = ColorFilter.tint(
                            hieroglyphTint.copy(alpha = if (seen) 1f else 0.3f)
                        )
                    )
                } else {
                    Text(
                        text = hiero.unicode.ifBlank { "?" },
                        fontSize = 24.sp,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.weight(1f),
                        color = if (seen) MaterialTheme.colorScheme.onSurfaceVariant else MaterialTheme.colorScheme.onSurfaceVariant.copy(
                            alpha = 0.3f
                        )
                    )
                }

                if (!hideCode) {
                    Text(
                        text = hiero.gardinerCode,
                        style = MaterialTheme.typography.labelSmall.copy(fontWeight = FontWeight.Bold),
                        textAlign = TextAlign.Center,
                        color = if (seen) MaterialTheme.colorScheme.onSurfaceVariant else MaterialTheme.colorScheme.onSurfaceVariant.copy(
                            alpha = 0.3f
                        )
                    )
                }
            }
        }

        if (isFavorite) {
            Image(
                painter = painterResource(Res.drawable.favourite_on),
                contentDescription = stringResource(Res.string.favorite_badge_desc),
                modifier = Modifier
                    .align(Alignment.TopEnd)
                    .padding(6.dp)
                    .size(16.dp),
                colorFilter = if (LocalIsDarkMode.current) {
                    ColorFilter.tint(Color.White.copy(alpha = 0.8f))
                } else {
                    null
                }
            )
        }
    }
}

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun HieroItemCardPreview() {
    AppTheme {
        Surface {
            HieroItemCard(
                hiero = Hiero(
                    gardinerCode = "A1",
                    unicode = "𓀀",
                    transliteration = "i",
                    ideogramma = "uomo",
                    determinativo = "uomo, me, mi",
                    mnemonico = "-",
                    ideogrammaEn = "man",
                    determinativoEn = "man, me, I",
                    mnemonicoEn = "-"
                ),
                seen = true,
                onClick = {}
            )
        }
    }
}
